import { NgTemplateOutlet } from "@angular/common";
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { MatIconButton } from "@angular/material/button";

import { TimelineBar } from "../timeline-bar/timeline-bar.component";
import { TimelineChip } from "../timeline-chip/timeline-chip.component";
import { TimelineNav } from "../timeline-nav/timeline-nav.component";
import {
  addDays,
  addMonths,
  DayKey,
  diffDays,
  formatDay,
  formatSpan,
  isWeekend,
  maxDay,
  minDay,
  mondayOf,
  monthStart,
  parseDay,
  weekdayShort,
} from "../timeline-dates";
import { BarView, chipsOn, entryAccessibleName, layoutBars, PackedItem, packRow } from "../timeline-layout";
import {
  clampWindowStart,
  lastWindowStartFor,
  TimelineDateStyle,
  TimelineEntry,
  TimelineLabelDensity,
  TimelineRange,
  TimelineRow,
  TimelineScale,
  TimelineTodayStyle,
} from "../timeline.model";

interface GanttRowView {
  row: TimelineRow;
  /** Label and suffix, for the tooltip and accessible names. */
  name: string;
  /** Grid line the label, backgrounds and today line run to. */
  rowEnd: number;
  gridRows: string;
  // Day scale with chips: bars in lanes above one row of chip stacks.
  bars: BarView[];
  cells: { column: number; entries: TimelineEntry[] }[];
  // Every other scale or style: everything packed into lanes.
  items: PackedItem[];
  /** Line style: each one-day entry's line, in its lane. */
  marks: { id: string; column: number; lane: number; color: string }[];
  /** Line style: a group row's date lines carried down from the date to the bottom of the row and through its items. */
  guides: { id: string; column: number; fromLane: number; color: string }[];
}

interface Band {
  key: string;
  text: string;
  column: number;
  span: number;
  current?: boolean;
}

const LABEL_PX = { normal: 200, compact: 150 };

/**
 * Rows down the side, one column per day of the window. Dumb view: the shell owns state. A row with a `detailId` has
 * a clickable label that selects that detail.
 *
 * `scale` sets the header (days, or week / month bands over the day columns); `dateStyle` draws one-day entries as
 * chips or as lines with small pills; `todayStyle` is the today line's style on the week and month scales;
 * `labelDensity` sets the row labels to two lines or one.
 */
@Component({
  selector: "lib-timeline-gantt",
  templateUrl: "./timeline-gantt.component.html",
  styleUrl: "./timeline-gantt.component.scss",
  imports: [MatIconButton, NgTemplateOutlet, TimelineBar, TimelineChip, TimelineNav],
  host: {
    "[style.--label-width]": "labelWidth() + 'px'",
    "[class.compact]": "compact()",
    "[class.one-line]": "labelDensity() === 'one-line'",
    "[class.packed]": "packed()",
    "[class.fill]": "fill()",
  },
})
export class TimelineGantt {
  readonly rows = input.required<TimelineRow[]>();
  readonly entries = input.required<TimelineEntry[]>();
  readonly windowStart = model.required<DayKey>();
  readonly windowDays = input.required<number>();
  /** The loaded range; with it the chart shows Today / Prev / Next whenever the window can move inside it. */
  readonly range = input<TimelineRange | null>(null);
  /** Where Today goes; this week (kept inside the range) when unset. */
  readonly homeStart = input<DayKey | null>(null);
  /** Off for an unattended view such as a wallboard. */
  readonly navigation = input(true);
  /** Fill the parent's height and scroll inside it with no scrollbar, keeping the nav bar and date header in view. */
  readonly fill = input(false);
  readonly today = input.required<DayKey>();
  readonly selectedId = input<string | null>(null);
  /** Tablet: narrower labels and one-line chips. */
  readonly compact = input(false);
  readonly scale = input<TimelineScale>("weeks");
  readonly dateStyle = input<TimelineDateStyle>("line");
  readonly todayStyle = input<TimelineTodayStyle>("dashed");
  readonly labelDensity = input<TimelineLabelDensity>("two-line");
  /** Width of a weekend day column compared with a weekday (1 = the same), e.g. 0.4 to favor the work week. */
  readonly weekendWidth = input(0.4);
  /** Accessible name of the chart, e.g. "Project Gantt". */
  readonly label = input("Gantt");
  readonly cornerLabel = input("");
  readonly emptyText = input("Nothing to show.");
  readonly entrySelected = output<string>();

  protected readonly entryAccessibleName = entryAccessibleName;

  protected readonly canPage = computed(() => {
    const range = this.range();
    return this.navigation() && !!range && lastWindowStartFor(range, this.windowDays()) > range.firstWindowStart;
  });
  protected readonly navTitle = computed(() => formatSpan(this.windowStart(), this.#windowEnd()));
  protected readonly atFirst = computed(() => {
    const range = this.range();
    return !range || this.windowStart() <= range.firstWindowStart;
  });
  protected readonly atLast = computed(() => {
    const range = this.range();
    return !range || this.windowStart() >= lastWindowStartFor(range, this.windowDays());
  });

  protected step(direction: 1 | -1): void {
    const range = this.range();
    if (range)
      this.windowStart.set(clampWindowStart(range, addDays(this.windowStart(), 7 * direction), this.windowDays()));
  }

  protected goToday(): void {
    const range = this.range();
    if (range)
      this.windowStart.set(this.homeStart() ?? clampWindowStart(range, range.todayWindowStart, this.windowDays()));
  }

  protected readonly labelWidth = computed(() => (this.compact() ? LABEL_PX.compact : LABEL_PX.normal));
  /** Day scale with chips is the original layout; everything else packs rows into lanes. */
  protected readonly packed = computed(() => this.scale() !== "days" || this.dateStyle() === "line");

  private readonly chart = viewChild<ElementRef<HTMLElement>>("chart");
  readonly #chartWidth = signal(0);

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const chart = this.chart()?.nativeElement;
      if (!chart) return;
      const observer = new ResizeObserver(([entry]) => this.#chartWidth.set(entry.contentRect.width));
      observer.observe(chart);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  /** Each day column's share of the timeline: 1 for a weekday, `weekendWidth` for a weekend day. */
  readonly #dayShares = computed(() =>
    Array.from({ length: this.windowDays() }, (_, i) =>
      isWeekend(addDays(this.windowStart(), i)) ? this.weekendWidth() : 1
    )
  );

  /** Each day column's width in px; packed rows center date lines in their day and run bars between day centers. */
  readonly #dayWidths = computed(() => {
    const shares = this.#dayShares();
    const total = shares.reduce((sum, share) => sum + share, 0);
    const timelinePx = Math.max(1, (this.#chartWidth() || 1200) - this.labelWidth());
    return shares.map((share) => (timelinePx * share) / total);
  });

  protected readonly days = computed(() =>
    Array.from({ length: this.windowDays() }, (_, i) => {
      const day = addDays(this.windowStart(), i);
      return {
        day,
        column: i + 2,
        weekday: weekdayShort(day),
        date: parseDay(day).getDate(),
        weekend: isWeekend(day),
        today: day === this.today(),
      };
    })
  );

  protected readonly gridColumns = computed(() =>
    this.weekendWidth() === 1
      ? `var(--label-width) repeat(${this.windowDays()}, minmax(0, 1fr))`
      : `var(--label-width) ${this.#dayShares()
          .map((share) => `minmax(0, ${share}fr)`)
          .join(" ")}`
  );

  readonly #windowEnd = computed(() => addDays(this.windowStart(), this.windowDays() - 1));
  readonly #column = (day: DayKey): number => diffDays(this.windowStart(), day) + 2;

  /** Week bands (clipped to the window): the header's lower tier and the row backgrounds on the week and month scales. */
  protected readonly weekBands = computed<Band[]>(() => {
    const start = this.windowStart();
    const end = this.#windowEnd();
    const bands: Band[] = [];
    for (let week = mondayOf(start); week <= end; week = addDays(week, 7)) {
      const first = maxDay(week, start);
      const last = minDay(addDays(week, 6), end);
      const sunday = addDays(week, 6);
      const sameMonth = parseDay(sunday).getMonth() === parseDay(week).getMonth();
      bands.push({
        key: week,
        text:
          this.scale() === "weeks"
            ? `${formatDay(week)}–${sameMonth ? parseDay(sunday).getDate() : formatDay(sunday)}`
            : String(parseDay(week).getDate()),
        column: this.#column(first),
        span: diffDays(first, last) + 1,
        current: this.today() >= week && this.today() <= sunday,
      });
    }
    return bands;
  });

  /** Each run of weekend days in the window, shaded like the day scale's weekend columns. */
  protected readonly weekendRuns = computed(() => {
    const runs: { key: string; column: number; span: number }[] = [];
    for (const day of this.days()) {
      const last = runs.at(-1);
      if (!day.weekend) continue;
      if (last && last.column + last.span === day.column) last.span++;
      else runs.push({ key: day.day, column: day.column, span: 1 });
    }
    return runs;
  });

  protected readonly monthBands = computed<Band[]>(() => {
    const start = this.windowStart();
    const end = this.#windowEnd();
    const bands: Band[] = [];
    for (let month = monthStart(start); month <= end; month = addMonths(month, 1)) {
      const first = maxDay(month, start);
      const last = minDay(addDays(addMonths(month, 1), -1), end);
      const date = parseDay(month);
      bands.push({
        key: month,
        text: `${date.toLocaleString("en-US", { month: "short" })} ${date.getFullYear()}`,
        column: this.#column(first),
        span: diffDays(first, last) + 1,
      });
    }
    return bands;
  });

  /** Grid column of the today line on the week and month scales, or null when today is outside the window. */
  protected readonly todayColumn = computed(() =>
    this.scale() !== "days" && this.today() >= this.windowStart() && this.today() <= this.#windowEnd()
      ? this.#column(this.today())
      : null
  );

  protected readonly rowViews = computed<GanttRowView[]>(() => {
    const byRow = new Map<string, TimelineEntry[]>();
    for (const entry of this.entries()) byRow.set(entry.rowId, [...(byRow.get(entry.rowId) ?? []), entry]);
    const days = this.days();
    const packed = this.packed();
    const lines = this.dateStyle() === "line";
    const dayWidths = this.#dayWidths();
    let groupLines: { id: string; column: number; color: string }[] = [];

    return this.rows().map((row): GanttRowView => {
      const rowEntries = byRow.get(row.id) ?? [];
      const name = row.labelSuffix ? `${row.label} ${row.labelSuffix}` : row.label;
      if (!packed) {
        const { bars, lanes } = layoutBars(rowEntries, this.windowStart(), this.windowDays());
        const cells = days
          .map((day) => ({ column: day.column, entries: chipsOn(rowEntries, day.day) }))
          .filter((cell) => cell.entries.length);
        return {
          row,
          name,
          rowEnd: lanes + 2,
          gridRows: `${lanes ? `repeat(${lanes}, 22px) ` : ""}minmax(36px, auto)`,
          bars,
          cells,
          items: [],
          marks: [],
          guides: [],
        };
      }

      const { items, lanes } = packRow(rowEntries, this.windowStart(), this.windowDays(), {
        scale: this.scale(),
        dateStyle: this.dateStyle(),
        dayWidths,
      });
      const points = lines ? items.filter((item) => !item.bar) : [];
      const marks = points.map((item) => ({
        id: item.entry.id,
        column: item.column,
        lane: item.lane,
        color: item.entry.color,
      }));
      let guides: GanttRowView["guides"];
      if (row.isGroup) {
        guides = marks.map((mark) => ({ id: mark.id, column: mark.column, fromLane: mark.lane, color: mark.color }));
        groupLines = marks;
      } else {
        guides = groupLines.map((line) => ({ ...line, fromLane: 1 }));
      }
      return {
        row,
        name,
        rowEnd: lanes + 1,
        gridRows: `repeat(${lanes}, minmax(${lines ? 20 : 26}px, auto))`,
        bars: [],
        cells: [],
        items,
        marks,
        guides,
      };
    });
  });
}
